import { useMemo } from "react";
import type { TableData, TableRowData } from "../types";

/**
 * Hook for managing table data structure and providing navigation methods
 */
export function useTableCollection(data: TableData) {
  const keyToIndexMap = useMemo(() => {
    const map = new Map<string | number, number>();
    data.rows.forEach((row, index) => {
      map.set(row.key, index);
    });
    return map;
  }, [data.rows]);

  const getItemByKey = useMemo(
    () =>
      (key: string | number): TableRowData | null => {
        const index = keyToIndexMap.get(key);
        if (index === undefined) return null;
        return data.rows[index];
      },
    [data.rows, keyToIndexMap]
  );

  const getItemAfter = useMemo(
    () =>
      (key: string | number): TableRowData | null => {
        const index = keyToIndexMap.get(key);
        if (index === undefined || index >= data.rows.length - 1) return null;
        return data.rows[index + 1];
      },
    [data.rows, keyToIndexMap]
  );

  const getItemBefore = useMemo(
    () =>
      (key: string | number): TableRowData | null => {
        const index = keyToIndexMap.get(key);
        if (index === undefined || index <= 0) return null;
        return data.rows[index - 1];
      },
    [data.rows, keyToIndexMap]
  );

  const getItemIndex = useMemo(
    () =>
      (key: string | number): number => {
        return keyToIndexMap.get(key) ?? -1;
      },
    [keyToIndexMap]
  );

  // Computed values
  const firstItem = useMemo(() => data.rows[0] || null, [data.rows]);
  const lastItem = useMemo(
    () => data.rows[data.rows.length - 1] || null,
    [data.rows]
  );
  const size = data.rows.length;

  // ARIA helpers
  const isHeaderHidden = data.columns[0]?.isHidden || false;
  const hasVisibleHeader = !isHeaderHidden;

  const getTotalRowCount = useMemo(() => {
    return size + (hasVisibleHeader ? 1 : 0);
  }, [size, hasVisibleHeader]);

  const getRowAriaIndex = useMemo(
    () => (rowIndex: number) => {
      return rowIndex + (hasVisibleHeader ? 2 : 1); // +2 if header exists and visible, +1 if not
    },
    [hasVisibleHeader]
  );

  return {
    // Data
    columns: data.columns,
    rows: data.rows,
    size,
    firstItem,
    lastItem,

    // ARIA helpers
    isHeaderHidden,
    hasVisibleHeader,
    getTotalRowCount,
    getRowAriaIndex,

    // Navigation functions
    getItemByKey,
    getItemAfter,
    getItemBefore,
    getItemIndex,
  };
}

export type TableCollection = ReturnType<typeof useTableCollection>;
